import { useState, type KeyboardEvent } from "react";
import type { Menu, Promotions } from "../../api/types";
import { t } from "../../i18n";
import { eurosToCents, formatAmount, formatEuro, parseAmount } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import { categorySizes, dayForCategory, WEEKDAYS, WEEK_ORDER } from "../promotions/promotions";
import { productsIn, sellableCategories } from "../sale/menu-view";

type Day = Promotions["day"];

interface Props {
  menu: Menu;
  day: Day;
  busy: boolean;
  onSave: (day: Day) => void;
  onClose: () => void;
}

const noEnter = (event: KeyboardEvent) => {
  if (event.key === "Enter") event.preventDefault();
};

/**
 * "Dagpromo instellen" (Furkan, 01/10/2026: dag, categorie en prijs moeten te wijzigen zijn): the days,
 * the category, the price per size (empty = no day price for that size), the products that do not take
 * part and the word on the tiles.
 */
export function DayPromotionDialog({ menu, day: start, busy, onSave, onClose }: Props) {
  const text = t.promotions.day;
  const [day, setDay] = useState<Day>(start);
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(start.prices).map(([size, euros]) => [size, formatAmount(eurosToCents(euros))])),
  );
  const [error, setError] = useState<string | null>(null);
  const sizes = categorySizes(menu, day.categoryId);
  const products = day.categoryId === null ? [] : productsIn(menu, day.categoryId);

  /** The lowest price of a size on the menu, as a hint next to the day price. */
  const menuPrice = (size: string) => {
    const amounts = products.flatMap((product) =>
      product.variants.filter((variant) => variant.label === size).map((variant) => eurosToCents(variant.price)),
    );
    return amounts.length > 0 ? Math.min(...amounts) : null;
  };

  const save = () => {
    const parsed: Record<string, number> = {};
    for (const size of sizes) {
      const typed = (prices[size] ?? "").trim();
      if (typed === "") continue;
      const cents = parseAmount(typed);
      if (cents === null || cents <= 0) {
        setError(text.badPrice(size));
        return;
      }
      parsed[size] = cents / 100;
    }
    if (Object.keys(parsed).length === 0) {
      setError(text.noPrice);
      return;
    }
    if (day.weekdays.length === 0) {
      setError(text.noDay);
      return;
    }
    const label = day.label.trim();
    if (label === "") {
      setError(text.noLabel);
      return;
    }
    onSave({ ...day, label, prices: parsed });
  };

  return (
    <Dialog
      title={text.title}
      size="wide"
      onClose={onClose}
      footer={
        <>
          {error !== null && (
            <span className="error-text" role="alert">
              {error}
            </span>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t.menuAdmin.cancel}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={save}>
            {text.save}
          </button>
        </>
      }
    >
      <div className="field">
        <span>{text.days}</span>
        <div className="daytogs" role="group" aria-label={text.days}>
          {WEEK_ORDER.map((weekday) => (
            <button
              key={weekday}
              type="button"
              className="btn tog"
              aria-pressed={day.weekdays.includes(weekday)}
              aria-label={WEEKDAYS[weekday]}
              onClick={() => {
                const on = day.weekdays.includes(weekday);
                setDay({
                  ...day,
                  weekdays: on
                    ? day.weekdays.filter((item) => item !== weekday)
                    : [...day.weekdays, weekday].sort((a, b) => a - b),
                });
              }}
            >
              {WEEKDAYS[weekday].slice(0, 2)}
            </button>
          ))}
        </div>
      </div>

      <div className="pegrid">
        <label className="field">
          {text.category}
          <select
            value={day.categoryId ?? ""}
            onChange={(event) => {
              const next = dayForCategory(day, menu, event.target.value);
              setDay(next);
              setPrices(Object.fromEntries(Object.entries(prices).filter(([size]) => size in next.prices)));
            }}
          >
            {day.categoryId === null && <option value="" />}
            {sellableCategories(menu).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>
            {text.label} <span className="note">{text.labelHint}</span>
          </span>
          <input
            value={day.label}
            maxLength={30}
            onKeyDown={noEnter}
            onChange={(event) => {
              setDay({ ...day, label: event.target.value });
            }}
          />
        </label>
      </div>

      <div className="field">
        <span>
          {text.prices} <span className="note">{text.pricesHint}</span>
        </span>
        <div className="dayprices">
          {sizes.map((size) => {
            const usual = menuPrice(size);
            return (
              <label key={size} className="opt-price">
                <span className="eur">{size} €</span>
                <input
                  className="num"
                  inputMode="decimal"
                  value={prices[size] ?? ""}
                  aria-label={text.priceOf(size)}
                  onKeyDown={noEnter}
                  onChange={(event) => {
                    setPrices({ ...prices, [size]: event.target.value });
                  }}
                />
                {usual !== null && <span className="note">{text.usual(formatEuro(usual))}</span>}
              </label>
            );
          })}
        </div>
      </div>

      <div className="field">
        <span>
          {text.except} <span className="note">{text.exceptHint}</span>
        </span>
        <div className="chips">
          {products.map((product) => {
            const out = day.excludeProductIds.includes(product.id);
            return (
              <button
                key={product.id}
                type="button"
                className="chip minus"
                aria-pressed={out}
                onClick={() => {
                  setDay({
                    ...day,
                    excludeProductIds: out
                      ? day.excludeProductIds.filter((id) => id !== product.id)
                      : [...day.excludeProductIds, product.id],
                  });
                }}
              >
                {product.name}
              </button>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
}
